import {
  Channel1,
  Channel10b,
  Channel11b,
  Channel12b,
  Channel13b,
  Channel16b,
  Channel17b,
  Channel18b,
  Channel19b,
  Channel2,
  Channel20b,
  Channel3,
  Channel3b,
  Channel4,
  Channel4b,
  Channel5,
  Channel6,
  Channel6b,
  Channel7,
  Channel7b,
  Channel8,
  Channel8b,
  Channel9b,
  hashString,
  hexToRgb,
  shadeColor
} from '@src/utils/colors';
import { normalizeChannelName as normalize } from './dtc-commerce-mmm-channel-helpers';

interface CanonicalChannel {
  color: string;
  aliases: readonly string[];
}

// Aliases mirror cdn.blisspointmedia.com/mmm/icon-mapping.json so a channel's color always
// matches its icon. Update both files together if a new channel or alias is added.
//
// Several aliases of one family can appear in the same run (Raymour's 'broadcast' + 'cable',
// Impossible Foods' 'national tv' + 'local tv'). buildRunChannelColors gives those siblings
// distinct shades of the family color, so mapping them all here is safe.
const CANONICAL_CHANNELS: readonly CanonicalChannel[] = [
  {
    color: Channel7,
    aliases: [
      'paid social',
      'social',
      'social media',
      'paid social networks',
      'paid social non core'
    ]
  },
  { color: Channel4, aliases: ['paid search', 'search', 'sem', 'paid sem'] },
  {
    color: Channel5,
    aliases: [
      'display',
      'programmatic display',
      'programmatic',
      'paid display',
      'standard display',
      'paid programmatic'
    ]
  },
  {
    color: Channel6,
    aliases: ['paid shopping', 'shopping', 'shoppable', 'product listing ads', 'pla']
  },
  { color: Channel1, aliases: ['email', 'email marketing', 'direct mail', 'email sms'] },
  {
    color: Channel2,
    aliases: [
      'streaming',
      'streaming video',
      'otv',
      'ctv',
      'ott',
      'connected tv',
      'over the top',
      'video ctv',
      'streaming tv',
      'ctv olv',
      'streaming video ott',
      'streaming video olv'
    ]
  },
  { color: Channel3, aliases: ['amazon', 'amazon ads'] },
  { color: Channel8, aliases: ['affiliate'] },
  { color: Channel19b, aliases: ['video', 'online video', 'digital video', 'olv', 'video olv'] },
  { color: Channel4b, aliases: ['youtube', 'paid youtube demand gen'] },
  {
    color: Channel3b,
    aliases: [
      'linear',
      'linear tv',
      'tv',
      'television',
      'linear television',
      'national tv',
      'local tv',
      'broadcast',
      'cable'
    ]
  },
  { color: Channel9b, aliases: ['influencer', 'influencer affiliate'] },
  { color: Channel11b, aliases: ['streaming audio', 'digital audio', 'audio'] },
  { color: Channel13b, aliases: ['podcast', 'podcasting'] },
  { color: Channel7b, aliases: ['seo', 'organic search'] },
  { color: Channel10b, aliases: ['native', 'native advertising', 'native ads'] },
  {
    color: Channel8b,
    aliases: [
      'out of home',
      'ooh',
      'outdoor',
      'dooh',
      'digital out of home',
      'transit',
      'national ooh',
      'local ooh'
    ]
  },
  { color: Channel18b, aliases: ['commerce search', 'retail search', 'retail media search'] },
  {
    color: Channel6b,
    aliases: [
      'commerce programmatic',
      'retail media programmatic',
      'retail programmatic',
      'retail media display',
      'commerce display'
    ]
  },
  {
    color: Channel16b,
    aliases: ['mobile app', 'app', 'mobile', 'app install', 'app installs', 'mobile app install']
  },
  { color: Channel17b, aliases: ['google discovery', 'discovery'] },
  {
    color: Channel20b,
    aliases: ['performance max', 'pmax', 'paid shopping pmax', 'pmax demandgen']
  },
  { color: Channel12b, aliases: ['cro', 'conversion rate optimization'] }
];

const FALLBACK_PALETTE = CANONICAL_CHANNELS.map(c => c.color);

const aliasToColor = new Map<string, string>();
CANONICAL_CHANNELS.forEach(({ color, aliases }) => {
  aliases.forEach(alias => aliasToColor.set(alias, color));
});

const hashedPaletteColor = (key: string): string =>
  FALLBACK_PALETTE[hashString(key) % FALLBACK_PALETTE.length];

// `runColors` is the per-run assignment from buildRunChannelColors. Pass it whenever the
// run is loaded so no two channels on the page share a color; without it (or for a channel
// it doesn't know) this falls back to the channel's static family or hashed color.
export const resolveChannelColor = (
  channel: string,
  runColors?: ReadonlyMap<string, string>
): string => {
  if (!channel) return FALLBACK_PALETTE[0];
  const key = normalize(channel);
  const assigned = runColors?.get(key);
  if (assigned) return assigned;
  return aliasToColor.get(key) ?? hashedPaletteColor(key);
};

// Returns `count` visually distinct shades centered on `baseHex`.
// shades[0] is the base color; subsequent entries alternate lighter / darker
// with growing intensity, capped so they stay legible against a white background.
const SHADE_STEP = 0.16;
const SHADE_MAX = 0.55;
// Past this many, generateChannelShades starts repeating the capped lightest/darkest shade.
const MAX_DISTINCT_SHADES = 2 * Math.ceil(SHADE_MAX / SHADE_STEP) + 1;
export const generateChannelShades = (baseHex: string, count: number): string[] => {
  if (count <= 0) return [];
  if (count === 1) return [baseHex];
  const shades: string[] = [baseHex];
  for (let i = 1; i < count; i++) {
    const stepSize = Math.min(SHADE_MAX, SHADE_STEP * Math.floor((i + 1) / 2));
    shades.push(shadeColor(baseHex, i % 2 === 1 ? stepSize : -stepSize));
  }
  return shades;
};

type Vec3 = readonly [number, number, number];

const toLinearRgb = (hex: string): Vec3 => {
  const toLinear = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const { r, g, b } = hexToRgb(hex);
  return [toLinear(r), toLinear(g), toLinear(b)];
};

const toOklab = ([r, g, b]: Vec3): Vec3 => {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  ];
};

// How red-blind (protan) and green-blind (deutan) viewers see a linear-RGB color, per Machado,
// Oliveira & Fernandes (2009) at full severity.
const COLOR_BLIND_SIMULATIONS: readonly (readonly Vec3[])[] = [
  [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998]
  ],
  [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881]
  ]
];

const simulate = (matrix: readonly Vec3[], [r, g, b]: Vec3): Vec3 => {
  const row = (i: number) =>
    Math.max(0, Math.min(1, matrix[i][0] * r + matrix[i][1] * g + matrix[i][2] * b));
  return [row(0), row(1), row(2)];
};

const oklabDistance = (a: Vec3, b: Vec3): number => {
  const [l1, a1, b1] = toOklab(a);
  const [l2, a2, b2] = toOklab(b);
  return 100 * Math.hypot(l1 - l2, a1 - a2, b1 - b2);
};

// The usual bars for telling two chart colors apart, as OKLab distance ×100.
const NORMAL_VISION_MIN_DISTANCE = 15;
const COLOR_BLIND_MIN_DISTANCE = 8;

// How far apart two colors are for whichever viewer finds them closest — normal vision or
// red-green color blindness — as a multiple of that viewer's bar. 1 or more reads as distinct.
export const colorSeparation = (a: string, b: string): number => {
  const linearA = toLinearRgb(a);
  const linearB = toLinearRgb(b);
  const colorBlind = COLOR_BLIND_SIMULATIONS.map(
    matrix =>
      oklabDistance(simulate(matrix, linearA), simulate(matrix, linearB)) / COLOR_BLIND_MIN_DISTANCE
  );
  return Math.min(oklabDistance(linearA, linearB) / NORMAL_VISION_MIN_DISTANCE, ...colorBlind);
};

export const areColorsDistinct = (a: string, b: string): boolean => colorSeparation(a, b) >= 1;

// The colors a channel's sub-channels take after the first. Every pair passes
// areColorsDistinct and holds 3:1 contrast on white. Six is the most the design-system palette
// allows while every pair still passes.
const SUB_CHANNEL_PALETTE = [Channel8, Channel10b, Channel4b, Channel4, Channel11b, Channel5];

// One color per sub-channel inside a channel's drawer, where every line belongs to the same
// channel and shades of one hue are too alike to tell apart. The first name keeps the channel's
// own color so it matches the rest of the page. Each later name takes the palette color most
// different from every color already used, skipping any too close to the channel's. Past the
// palette, names fall back to shades of the channel's color. `orderedNames` should be sorted by
// importance.
export const buildSubChannelColorMap = (
  channelColor: string,
  orderedNames: readonly string[]
): Map<string, string> => {
  const map = new Map<string, string>();
  if (orderedNames.length === 0) return map;
  const [first, ...rest] = orderedNames;
  map.set(first, channelColor);
  const used = [channelColor];
  const remaining = SUB_CHANNEL_PALETTE.filter(color => areColorsDistinct(color, channelColor));
  const overflowCount = Math.max(0, rest.length - remaining.length);
  const overflowShades = generateChannelShades(channelColor, overflowCount + 1).slice(1);
  const nearest = (color: string) => Math.min(...used.map(u => colorSeparation(color, u)));
  rest.forEach(name => {
    if (remaining.length === 0) {
      map.set(name, overflowShades.shift() ?? channelColor);
      return;
    }
    // Ties keep palette order.
    const pick = remaining.reduce((best, color) => (nearest(color) > nearest(best) ? color : best));
    remaining.splice(remaining.indexOf(pick), 1);
    used.push(pick);
    map.set(name, pick);
  });
  return map;
};

export interface ChannelSpend {
  channel: string;
  spend: number;
}

// First palette color not already on the page, starting from the channel's hashed color so
// an unrecognized channel keeps today's color whenever nothing else has claimed it.
const pickUnusedColor = (key: string, used: ReadonlySet<string>): string => {
  const start = hashString(key) % FALLBACK_PALETTE.length;
  for (let i = 0; i < FALLBACK_PALETTE.length; i++) {
    const candidate = FALLBACK_PALETTE[(start + i) % FALLBACK_PALETTE.length];
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  // Every palette color is taken: fall back to shades, starting from the hashed color.
  // Shades cap out after a handful per base, so walk the whole palette.
  for (let i = 0; i < FALLBACK_PALETTE.length; i++) {
    const base = FALLBACK_PALETTE[(start + i) % FALLBACK_PALETTE.length];
    const shade = generateChannelShades(base, MAX_DISTINCT_SHADES).find(
      s => !used.has(s.toLowerCase())
    );
    if (shade) return shade;
  }
  return FALLBACK_PALETTE[start];
};

// One color per channel for a whole run, so no two channels on the page share a color while
// every platform/feature under a channel keeps that channel's color:
// - a recognized channel takes its family color; when several channels of one family are in
//   the run, the biggest spender keeps the pure color and the rest get shades of it;
// - an unrecognized channel takes a palette color nothing else in the run is using.
// Keyed by normalized channel name. Spend should cover the whole run (not a date window) so
// colors don't shuffle when the date range changes.
export const buildRunChannelColors = (channels: readonly ChannelSpend[]): Map<string, string> => {
  const spendByKey = new Map<string, number>();
  channels.forEach(({ channel, spend }) => {
    if (!channel) return;
    const key = normalize(channel);
    spendByKey.set(key, (spendByKey.get(key) ?? 0) + spend);
  });
  const keysBySpend = [...spendByKey.entries()]
    .sort(([keyA, a], [keyB, b]) => b - a || keyA.localeCompare(keyB))
    .map(([key]) => key);

  const familyMembers = new Map<string, string[]>();
  const unrecognized: string[] = [];
  keysBySpend.forEach(key => {
    const familyColor = aliasToColor.get(key);
    if (!familyColor) {
      unrecognized.push(key);
      return;
    }
    familyMembers.set(familyColor, [...(familyMembers.get(familyColor) ?? []), key]);
  });

  const colors = new Map<string, string>();
  const used = new Set<string>();
  const assign = (key: string, color: string) => {
    colors.set(key, color);
    used.add(color.toLowerCase());
  };
  familyMembers.forEach((keys, familyColor) => {
    const shades = generateChannelShades(familyColor, keys.length);
    keys.forEach((key, i) => assign(key, shades[i]));
  });
  unrecognized.forEach(key => assign(key, pickUnusedColor(key, used)));
  return colors;
};
